import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { Questionnaire } from '../../interfaces/questionnaire.interface';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useForm, FieldValues } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import { createQuestionnaire, updateQuestionnaire } from '../../app/store/questionnaireSlice';
import { StandardDialogActions } from '../Modals/StandardDialog';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { QuestionnaireTypes, questionnaireTypes, fetchQuestionnairesByVersion, questionnaireFeatureFlagCodename } from '../../services/questionnaires';
import { isEmpty, isValidDateFormat } from '../../utils/adminDashboardUtils';
import moment from 'moment';

export default function QuestionnaireForm(props: { handleClose: any, refresh: any }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();
  const watchedVersion = watch('version');
  const { error } = useAppSelector(state => state.questionnaires);
  const dispatch = useAppDispatch();
  const onSubmit = async (data: FieldValues) => {
    const archiveDate = isEmpty(data.archiveDate)
      ? null
      : moment(data.archiveDate, 'YYYY-MM-DD').startOf('day').toISOString();
    const sortOrder = data.sortOrder === '' || data.sortOrder === undefined ? undefined : Number(data.sortOrder);
    const result = await dispatch(createQuestionnaire({ ...data, archiveDate, sortOrder }));
    // Close only when the API accepted it; otherwise stay open showing the field errors.
    if (createQuestionnaire.fulfilled.match(result)) {
      reset({ title: '', detail: '', isPublished: undefined, type: undefined });
      props.refresh();
      props.handleClose();
    }
  };
  // Checked against the server: the listing is paginated, so the store is not the full set.
  const versionIsAvailable = async (version: string, type?: string) => {
    try {
      const { results } = await fetchQuestionnairesByVersion(version);
      return !results.some(q => q.type === type);
    } catch (e) {
      console.error(e);
      return true;
    }
  };


  return (
    <Box component='form' sx={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>How new questionnaires roll out</AlertTitle>
        This creates a <strong>single questionnaire</strong> of the chosen type. A complete release is a
        <strong> version group</strong>: one MPA Best Practice, one Site Baseline and one Application Baseline
        questionnaire sharing the same version. To create all three from an existing version, use the
        duplicate action on the questionnaire list instead.
        <ul style={{ margin: '0.5em 0 0', paddingLeft: '1.25em' }}>
          <li>Leave <strong>Publish</strong> unchecked to keep the questionnaire hidden while it is prepared.</li>
          <li>
            Saving an MPA Best Practice questionnaire automatically creates the feature flag
            {' '}<code>{questionnaireFeatureFlagCodename(watchedVersion || '<version>')}</code>. Grant it to
            specific users or companies under <strong>Admin &rarr; Feature Flags</strong> &mdash; only they can see
            and start the questionnaire while it is unpublished.
          </li>
          <li>Publishing later makes it visible to <strong>everyone</strong>; unpublishing restricts it to flag holders again.</li>
          <li>
            While unpublished, the questionnaire always serves its <strong>latest content</strong> &mdash;
            trial users get your edits whenever they open or reload it, with nothing to trigger.
          </li>
          <li>
            Once published, users receive a <strong>stored copy</strong> of the content instead of reading
            it live. The copy is rebuilt automatically when you publish or save this form, but after
            editing the Domains, Topics, Best Practices, Questions or Answers of a published
            questionnaire you must run <strong>Update Stored Content</strong> from the questionnaire
            list &mdash; until then, users keep receiving the old copy.
          </li>
        </ul>
      </Alert>
      <Grid container sx={{ width: '100%' }} rowSpacing={2}>
        <Grid item xs={12}>
          <TextField
            required
            id="title"
            label="Questionnaire Title"
            sx={{ width: '100%' }}
            autoFocus
            {...register('title')}
            error={!!error?.title}
            helperText={!!error?.title && error.title[0]}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="description"
            label="Description"
            multiline
            rows={3}
            sx={{ width: '100%' }}
            {...register('description')}
            error={!!error?.description}
            helperText={!!error?.description && error.description[0]}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id='select-type-label'>Type</InputLabel>
            <Select
              labelId='select-type-label'
              id='select-type'
              label='Type'
              {...register('type')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {questionnaireTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} pl={2}>
          <FormControlLabel
            id='isPublished'
            control={<Checkbox {...register('isPublished')} name='isPublished'/>}
            label="Publish"
            labelPlacement="end"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="bestPracticeGuidanceLink"
            label="IG and BP Guidance document"
            sx={{ width: '100%' }}
            autoFocus
            {...register('bestPracticeGuidanceLink')}
            error={!!error?.bestPracticeGuidanceLink}
            helperText={!!error?.bestPracticeGuidanceLink && error.bestPracticeGuidanceLink[0]}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            required
            id="version"
            label="Group Version"
            placeholder="e.g. 5.3.2"
            sx={{ width: '100%' }}
            error={!!errors?.version || !!error?.version}
            helperText={
              (errors?.version?.message as string | undefined) ||
              (!!error?.version && error.version[0]) ||
              'The version this questionnaire belongs to, as major.minor.patch (e.g. 5.3.2). Each type can only have one questionnaire per version.'
            }
            {...register('version', {
              validate: async (value, formValues) =>
                (await versionIsAvailable(value, formValues.type)) ||
                'A questionnaire of this type already uses this version.',
            })}
          />
        </Grid>
        <Grid item xs={6} pl={2}>
          <TextField
            id="archiveDate"
            label="Archive Date"
            type="date"
            sx={{ width: '100%' }}
            InputLabelProps={{ shrink: true }}
            error={!!errors?.archiveDate}
            helperText={(errors?.archiveDate?.message as string | undefined)
              || 'Optional. The questionnaire locks on this date.'}
            {...register('archiveDate', {
              validate: (v) => {
                if (isEmpty(v)) return true;
                return isValidDateFormat(v);
              },
            })}
          />
          <Tooltip
            placement="top"
            title="When an Archive Date is set, the questionnaire is locked. In-progress surveys cannot be completed after this date."
          >
            <IconButton size="small" aria-label="Archive date info">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="workflowLabel"
            label="Workflow Label"
            placeholder="e.g. Questionnaire v5.3.2"
            sx={{ width: '100%' }}
            helperText="The display name shown wherever this version appears across TPN+ (dropdowns, reports, the directory). Applies to MPA Best Practice questionnaires; leave blank to use 'Questionnaire v<version>'."
            {...register('workflowLabel')}
          />
        </Grid>
        <Grid item xs={6} pl={2}>
          <TextField
            id="sortOrder"
            label="Sort Order"
            type="number"
            sx={{ width: '100%' }}
            helperText="Where this version sits in version lists — lower numbers first (oldest). Applies to MPA Best Practice questionnaires; leave blank to place it after all existing versions."
            {...register('sortOrder')}
          />
        </Grid>
      </Grid>
      <StandardDialogActions>
        <Button variant="outlined" onClick={() => props.handleClose()}>Cancel</Button>
        <Button type="submit" variant="contained">Save</Button>
      </StandardDialogActions>
    </Box>
  );
}

export function QuestionnaireEditForm(props: { questionnaire: Questionnaire, handleClose: any, refresh?: any }) {
  let [questionnaire] = useState(props.questionnaire);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: questionnaire.title,
      description: questionnaire.description,
      isPublished: questionnaire.isPublished,
      type: questionnaire.type,
      bestPracticeGuidanceLink: questionnaire.bestPracticeGuidanceLink,
      includeInReporting: questionnaire.includeInReporting,
      archiveDate: questionnaire.archiveDate ? moment(questionnaire.archiveDate).format('YYYY-MM-DD') : '',
      workflowLabel: questionnaire.workflowLabel ?? '',
      sortOrder: questionnaire.sortOrder ?? 0,
    },
  });
  const dispatch = useAppDispatch();
  const toDomainIds = (q: Questionnaire) => q.domains.map(d => (typeof d === 'number' ? d : d.id));

  const onSubmit = useCallback(async (data: FieldValues) => {
    // The listing is paginated; fetch the whole version group so shared fields
    // (e.g. archive date) propagate to every member.
    const relatedQuestionnaires = (await fetchQuestionnairesByVersion(String(questionnaire.version))).results;

    const archiveDate = isEmpty(data.archiveDate)
      ? null
      : moment(data.archiveDate, 'YYYY-MM-DD').startOf('day').toISOString();

    const sortOrder = data.sortOrder === '' || data.sortOrder === undefined ? 0 : Number(data.sortOrder);

    const updatedQuestionnaires = relatedQuestionnaires.map((q) => {
      const domains = toDomainIds(q);

      const updatedQuestionnaire = q.id === questionnaire.id
        ? { ...q, ...data, archiveDate, domains, sortOrder }
        : { ...q, archiveDate, domains };

      return dispatch(updateQuestionnaire(updatedQuestionnaire));
    });

    const results = await Promise.all(updatedQuestionnaires);
    // Close only when every group member saved; otherwise stay open showing the errors.
    if (results.every((result) => updateQuestionnaire.fulfilled.match(result))) {
      props.handleClose();
      if (props.refresh) props.refresh();
    }
  }, [dispatch, questionnaire]);

  const { error } = useAppSelector(state => state.questionnaires);

  return (
    <Box component='form' sx={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
      {questionnaire.isPublished && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This questionnaire is published</AlertTitle>
          Users receive a <strong>stored copy</strong> of its content rather than reading it live.
          Saving this form rebuilds that copy automatically, but edits to Domains, Topics, Best
          Practices, Questions or Answers do <strong>not</strong> reach users until you run
          {' '}<strong>Update Stored Content</strong> from the questionnaire list.
        </Alert>
      )}
      <Grid container sx={{ width: '100%' }} rowSpacing={2}>
        <Grid item xs={12}>
          <TextField
            required
            id="title"
            label="Questionnaire Title"
            sx={{ width: '100%' }}
            autoFocus
            {...register('title')}
            error={!!error?.title}
            helperText={!!error?.title && error.title[0]}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="description"
            label="Description"
            multiline
            rows={3}
            sx={{ width: '100%' }}
            {...register('description')}
            error={!!error?.description}
            helperText={!!error?.description && error.description[0]}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id='select-type-label'>Type</InputLabel>
            <Select
              labelId='select-type-label'
              id='select-type'
              label='Type'
              {...register('type')}
              defaultValue={questionnaire.type ? questionnaire.type : null}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {questionnaireTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} pl={2}>
          <FormControlLabel
            id={`isPublished-${questionnaire.id}`}
            control={<Checkbox defaultChecked={questionnaire.isPublished} {...register('isPublished')} name='isPublished'/>}
            label="Publish"
            labelPlacement="end"
          />
          <FormControlLabel
            id={`includeInReporting-${questionnaire.id}`}
            control={<Checkbox defaultChecked={questionnaire.includeInReporting} {...register('includeInReporting')} name='includeInReporting'/>}
            label="Include in Reporting"
            labelPlacement="end"
          />
          {questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ maxWidth: 280 }}>
              Published questionnaires are visible to everyone. While unpublished, only users granted the
              {' '}<code>{questionnaireFeatureFlagCodename(questionnaire.version)}</code> feature flag
              (Admin &rarr; Feature Flags) can see and start it.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="bestPracticeGuidanceLink"
            label="IG and BP Guidance document"
            sx={{ width: '100%' }}
            autoFocus
            {...register('bestPracticeGuidanceLink')}
            error={!!error?.bestPracticeGuidanceLink}
            helperText={!!error?.bestPracticeGuidanceLink && error.bestPracticeGuidanceLink[0]}
          />
        </Grid>
        <Grid item xs={12}>
         <TextField
          id="archiveDate"
          label="Archive Date"
          type="date"
          size="small"
          sx={{ width: 210 }}
          disabled={questionnaire.type !== QuestionnaireTypes.MPA_BEST_PRACTICE}
          InputLabelProps={{ shrink: true }}
          error={!!errors?.archiveDate}
          helperText={errors?.archiveDate?.message as string | undefined}
          {...register('archiveDate', {
            validate: (v) => {
              if (isEmpty(v)) return true;
              return isValidDateFormat(v);
            },
          })}
        />
          <Tooltip
            placement="top"
            title="When an Archive Date is set, the questionnaire is locked. In-progress surveys cannot be completed after this date."
          >
            <IconButton size="small" aria-label="Archive date info">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        {questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE && (
          <>
            <Grid item xs={6}>
              <TextField
                id="workflowLabel"
                label="Workflow Label"
                sx={{ width: '100%' }}
                helperText="The display name shown wherever this version appears across TPN+ (dropdowns, reports, the directory)."
                {...register('workflowLabel')}
              />
            </Grid>
            <Grid item xs={6} pl={2}>
              <TextField
                id="sortOrder"
                label="Sort Order"
                type="number"
                sx={{ width: '100%' }}
                helperText="Where this version sits in version lists — lower numbers first (oldest)."
                {...register('sortOrder')}
              />
            </Grid>
          </>
        )}
      </Grid>
      <StandardDialogActions>
        <Button variant="outlined" onClick={() => props.handleClose()}>Cancel</Button>
        <Button type="submit" variant="contained">Save</Button>
      </StandardDialogActions>
    </Box>
  );
}